import { JsonRpcProvider, ZeroAddress } from 'ethers'

import AmbireFactory from '../../../contracts/compiled/AmbireFactory.json'
import { AMBIRE_ACCOUNT_FACTORY, DEPLOYLESS_SIMULATION_FROM } from '../../consts/deploy'
import { getSmartAccount, getSpoof } from '../account/account'
import { callToTuple } from '../accountOp/accountOp'
import { getActivatorCall } from '../userOperation/userOperation'
import { DeploylessMode, fromDescriptor } from './deployless'

const TRANSIENT_RPC_ERROR_CODES = ['TIMEOUT', 'SERVER_ERROR', 'NETWORK_ERROR']
const TRANSIENT_RPC_ERROR_MESSAGE =
  /no response|rpc-timeout|timed? ?out|failed to fetch|network ?error|socket hang up|ECONNRESET|ETIMEDOUT|ENOTFOUND|too many requests|rate.?limit|\b429\b|bad gateway|service unavailable|gateway timeout/i

/**
 * ZKX: an RPC that could not be reached says nothing about whether it supports
 * state override. Reporting those as "unsupported" got persisted as
 * `rpcNoStateOverride: true`, which moved every simulation on the chain to the
 * deployless proxy - where EOA estimation always fails - so a single timeout
 * broke Swap on Arbitrum for good (Trello #507).
 */
export const isTransientRpcError = (e: any): boolean => {
  if (!e) return false
  if (TRANSIENT_RPC_ERROR_CODES.includes(e.code)) return true
  if (typeof e.statusCode === 'number' && (e.statusCode === 429 || e.statusCode >= 500)) return true

  return TRANSIENT_RPC_ERROR_MESSAGE.test(`${e.shortMessage || ''} ${e.message || ''}`)
}

// simulate a deployless call to the given provider.
// if the call is successful, it means Ambire smart accounts are supported
// on the given network
export async function getSASupport(
  provider: JsonRpcProvider
): Promise<{ addressMatches: boolean; supportsStateOverride: boolean }> {
  const smartAccount = await getSmartAccount(
    [
      {
        addr: DEPLOYLESS_SIMULATION_FROM,
        hash: '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'
      }
    ],
    []
  )
  const deploylessOptions = {
    blockTag: 'latest',
    from: DEPLOYLESS_SIMULATION_FROM,
    // very important to send to the AMBIRE_ACCOUNT_FACTORY
    // or else the SA address won't match
    to: AMBIRE_ACCOUNT_FACTORY,
    mode: DeploylessMode.StateOverride
  }
  const deployless = fromDescriptor(provider, AmbireFactory, true)
  let supportsStateOverride = true
  const result = await deployless
    .call(
      'deployAndExecute',
      [
        smartAccount.creation!.bytecode,
        smartAccount.creation!.salt,
        [callToTuple(getActivatorCall(smartAccount.addr))],
        getSpoof(smartAccount)
      ],
      deploylessOptions
    )
    .catch((e: any) => {
      if (e?.message?.includes('no response')) {
        throw new Error('no response')
      }
      // Let the caller retry / flag the RPC instead of recording a verdict.
      if (isTransientRpcError(e)) throw e

      // the RPC answered but the simulation failed: our smart accounts
      // will most likely not work on this chain
      supportsStateOverride = false
      return [ZeroAddress]
    })

  return {
    addressMatches: result === smartAccount.addr,
    supportsStateOverride
  }
}
